<template>
    <div>
        <div class="form-group">
            <div id="loader" v-if="isLoading">
                <div class="spinner"></div>
            </div>

        </div>

        <div class="form-row">
            <div class="form-group col-lg-6">
                <div class="mb-3">
                    <h3>Your name</h3>
                    <label for="pronouns" class="form-label"><b>My personal pronouns</b></label>
                    <p>How would you like us to identify you?</p>
                    <select
                        data-test="pronouns-select"
                        class="form-control form-select"
                        id="pronouns"
                        name="pronouns"
                        v-model="formRef.pronouns"
                    >
                        <option value=""></option>
                        <option value="He/Him/His/Himself">
                            He/Him/His/Himself
                        </option>
                        <option value="She/Her/Hers/Herself">
                            She/Her/Hers/Herself
                        </option>
                        <option value="They/Them/Theirs/Themself">
                            They/Them/Theirs/Themself
                        </option>
                        <option value="Ze/Zir/Zirs/Zirself">
                            Ze/Zir/Zirs/Zirself
                        </option>
                    </select>
                </div>
                <div class="mb-3">
                    <label for="first_name"
                    class="form-label"
                    ><b>First name </b><i class="fas fa-asterisk"></i
                    ></label>
                    <input
                        data-test="first-name-input"
                        type="text"
                        class="form-control"
                        id="first_name"
                        name="first_name"
                        autocomplete="given-name"
                        v-model="formRef.first_name"
                        required
                    />
                    <span
                        data-test="first-name-error-span"
                        class="enquiry-errors"
                        v-if="formRef.errors.has('first_name')"
                        v-text="formRef.errors.get('first_name')"
                        role="alert"
                        aria-live="assertive"
                    ></span>
                </div>
                <div class="mb-3">
                    <label for="last_name"
                    class="form-label"
                    ><b>Last name </b><i class="fas fa-asterisk"></i
                    ></label>
                    <input
                        data-test="last-name-input"
                        type="text"
                        class="form-control"
                        id="last_name"
                        name="last_name"
                        autocomplete="family-name"
                        v-model="formRef.last_name"
                        required
                    />
                    <span
                        data-test="last-name-error-span"
                        class="enquiry-errors"
                        v-if="formRef.errors.has('last_name')"
                        v-text="formRef.errors.get('last_name')"
                        role="alert"
                        aria-live="assertive"
                    ></span>
                </div>
            </div>
        </div>

        <div class="form-row">
            <div class="form-group col-lg-6">
                <h3>Your date of birth</h3>
                <h5 data-test="age-field-description">The King’s Trust supports people aged between:</h5>
                    <ul>
                        <li>18 and 30 on our Enterprise programme</li>
                        <li>16 and 30 on all other programmes.</li>
                    </ul>
                    <p>If you’re outside of these age ranges, we won’t be able to book you on to the Information Session.</p>
                    <label for="date_of_birth" class="form-label"
                    ><b>Date of birth (DD/MM/YYYY) </b><i class="fas fa-asterisk"></i
                    ></label>
                    <div>
                        <div class="date-input-field">
                            <div>
                            <label class="input-label form-label" for="dob-day">
                                <b>Day</b>
                            </label>
                            <input
                                v-model="formRef.day"
                                class="form-control input-width"
                                id="dob-day"
                                @input="moveToNextField($event)"
                                name="dob-day"
                                type="text"
                                inputmode="numeric"
                                maxlength="2"
                            >
                            </div>
                        </div>
                        <div class="date-input-field">
                            <div>
                            <label class="input-label form-label" for="dob-month">
                                <b>Month</b>
                            </label>
                            <input
                                v-model="formRef.month"
                                class="form-control input-width"
                                id="dob-month"
                                @input="moveToNextField($event)"
                                name="dob-month"
                                type="text"
                                inputmode="numeric"
                                maxlength="2"
                            >
                            </div>
                        </div>
                        <div class="date-input-field">
                            <div>
                            <label class="input-label form-label" for="dob-year">
                                <b>Year</b>
                            </label>
                            <input
                                v-model="formRef.year"
                                class="form-control input-width-2"
                                id="dob-year"
                                name="dob-year"
                                type="text"
                                inputmode="numeric"
                                maxlength="4"
                            >
                            </div>
                        </div>
                    </div>
                    <span
                        data-test="dob-error-span"
                        class="enquiry-errors"
                        v-if="formRef.errors.has('dob')"
                        v-text="formRef.errors.get('dob')"
                        role="alert"
                        aria-live="assertive"
                    >
                    </span>
                </div>
        </div>
        <div class="form__buttons form-group py-3">
            <button
                type="button"
                class="btn btn-secondary me-2"
                @click.prevent="goBack()"
                data-test="btn-back"
            >
                Go back
            </button>
            <button
                class="btn btn-primary"
                @click.prevent="nextPage()"
                data-test="btn-next"
            >
                Next
            </button>
        </div>

    </div>

</template>

<script setup>
import { format, isValid, parse } from 'date-fns';
import { enGB } from 'date-fns/locale';
import Form from "../../../../Form.js";
import { getAge } from "../dobChecker.js";
import { ref, markRaw } from "vue";

const props = defineProps({
    step: Number,
    formData: Object,
    programme: String
})

const emit = defineEmits(['goBackAStep', 'submitButton'])

let formRef = markRaw(ref(new Form({
    pronouns: null,
    first_name: null,
    last_name: null,
    dob: null,
    day: null,
    month: null,
    year: null,
    programme: props.programme
}, props.formData)))

const isLoading = ref(false)

function goBack() {
    emit('goBackAStep')
}

function moveToNextField(event) {
    let nextElement = null

    if (event.target.value.length == Number(event.target.attributes['maxlength'].value)) {
        if (event.target.id == 'dob-day') {
            nextElement = document.getElementById("dob-month")
        } else if (event.target.id == 'dob-month') {
            nextElement = document.getElementById("dob-year")
        }
        if (nextElement) nextElement.focus()
    }
}

function isDobValid() {
        let age = getAge(formRef.value.dob, formRef.value.programme);

        if (age === "incomplete" || age === false) {
            if(formRef.value.programme === 'enterprise') {
                formRef.value.errors.record({ 'dob': 'Please enter a valid date of birth'})
                Swal.fire({
                    text: 'Are you aged between 18 – 30 years old? If so, please re-enter your date of birth in this format: date/month/year (dd/mm/yyyy)',
                    confirmButtonText: 'OK'
                })
            } else {
                formRef.value.errors.record({ 'dob': 'Please enter a valid date of birth'})
                Swal.fire({
                    text: 'Are you aged between 16 – 30 years old? If so, please re-enter your date of birth in this format: date/month/year (dd/mm/yyyy)',
                    confirmButtonText: 'OK'
                })
            }
            return false;
        }
        return true;
}
function nextPage() {
    let dob
    if (formRef.value.month && formRef.value.day && formRef.value.year) {
        dob = formRef.value.day + '/' + formRef.value.month + '/' + formRef.value.year
    } else {
        dob = null
    }
    const parsed = dob ? parse(dob, 'P', new Date(), { locale: enGB }) : null
    const validDate = parsed !== null && isValid(parsed)
    formRef.value.dob = validDate ? format(new Date(parsed), 'dd/MM/yyyy') : null

    isLoading.value = true;
    return formRef.value
        .post("/registration/step2")
        .then(() => {
            if (isDobValid() == false) return
            emit("submitButton", {
                data: {
                    pronouns: formRef.value.pronouns,
                    first_name: formRef.value.first_name,
                    last_name: formRef.value.last_name,
                    day: formRef.value.day,
                    month: formRef.value.month,
                    year: formRef.value.year,
                    dob: formRef.value.dob
                }
            });
        })
        .catch(err => {
            isDobValid();
            formRef.value.errors.record(err);
        })
        .finally(() => {
            isLoading.value = false
        });
}
</script>
<style>
.date-input-field {
    display: inline-block;
    margin-right: 20px;
    margin-bottom: 0;
}
.input-width {
    max-width: 2.75em !important;
}
.input-width-2 {
    max-width: 4em !important;
}
.input-label {
    display: block;
}
</style>
